/**
 * What every page inside the frame can rely on: a signed in student, and the
 * profile and plan the API returned for them. A page is never rendered without one, so
 * useSession never has to answer "and if nobody is signed in".
 */

import { createContext } from "preact";
import { useContext } from "preact/hooks";
import type { PlanSummary, Profile } from "@ls/shared";

export interface SessionApi {
  readonly profile: Profile;
  /** The plan the student is on. Every account has one: Free until it takes another (day 35). */
  readonly plan: PlanSummary;
  /** Replace the profile after a change the API confirmed. */
  setProfile(profile: Profile): void;
  /** Ask the API again for the profile and plan, as after a purchase, without leaving the page. */
  refresh(): Promise<void>;
  signOut(): void;
}

export const SessionContext = createContext<SessionApi | null>(null);

export function useSession(): SessionApi {
  const session = useContext(SessionContext);
  if (!session) {
    throw new Error("useSession was called outside the app frame, which only renders for a live session.");
  }
  return session;
}

/** "Asha Rao" gives "Asha". Falls back to the address, which every account has. */
export function firstName(profile: Profile): string {
  const first = profile.name?.trim().split(/\s+/)[0];
  if (first) return first.charAt(0).toUpperCase() + first.slice(1);
  return profile.email.split("@")[0] ?? profile.email;
}

export function yearLabel(year: Profile["year"]): string | null {
  if (year === "1") return "1st year";
  if (year === "2") return "2nd year";
  if (year === "3") return "3rd year";
  return null;
}
